<template>
  <v-card class="pdfCard">
    <div v-if="isLoading">
      <v-progress-circular indeterminate size="14" width="2" class="mr-1" color="primary" />
      {{ $t('components.pdfViewer.loading') }}
    </div>

    <div v-else>
      <span v-if="showAllPages">
        {{ pageCount }} page(s)
      </span>

      <span v-else>
        <button
          :disabled="page <= 1"
          @click="page--"
        >
          ❮
        </button>
        {{ page }} / {{ pageCount }}
        <button
          :disabled="page >= pageCount"
          @click="page++"
        >
          ❯
        </button>
      </span>

      <label class="float-right">
        <input v-model="showAllPages" type="checkbox">
        {{ $t('components.pdfViewer.showAllPages') }}
      </label>
    </div>

    <ScrollBar
      class="pdfContainer"
      :class="{ loaded: !isLoading }"
      max-height="calc(80vh - 48px)"
      height="calc(100vh - 330px)"
    >
      <vue-pdf-embed
        ref="pdfRef"
        class="pdfViewer"
        v-if="embeddedContentUrl"
        :class="{ loaded: !isLoading }"
        :source="embeddedContentUrl"
        :page="page"
        @rendered="handleDocumentRender"
        :width="width"
      />
    </scrollbar>

    <div v-if="fullAnalysis.documentDetails.downloadAuthorized && embeddedContentUrl"
         class="documentInfo mt-4"
    >
      <a target="_blank" :href="originalContentUrl" class="d-flex justify-end mr-3">
        {{ $t('views.portfolio.seeOriginalContent') }}
      </a>
    </div>
  </v-card>
</template>

<script>
import { getDownloadUrlByPath } from '@/shared/firebase/storageFunctions';
import ScrollBar from '@/shared/components/scrollbar/ScrollBar';
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';

export default {
  name: 'PdfViewer',
  data() {
    return {
      embeddedContentUrl: undefined,
      originalContentUrl: undefined,
      isLoading: true,
      page: undefined,
      pageCount: 1,
      showAllPages: true,
    };
  },
  props: {
    fullAnalysis: {
      type: Object,
      required: true,
    },
    width: {
      type: Number,
      required: true,
    },
  },
  components: {
    VuePdfEmbed,
    ScrollBar,
  },
  methods: {
    handleDocumentRender() {
      this.isLoading = false;
      this.pageCount = this.$refs.pdfRef.pageCount;
    },
    async loadDocument() {
      const { documentId, contentType } = this.fullAnalysis.documentDetails;

      if (!documentId) {
        setTimeout(() => { this.loadDocument(); }, 1000);
        return;
      }

      const documentPath = `documents/${documentId}`;
      try {
        this.originalContentUrl = await getDownloadUrlByPath(documentPath);
      } catch (e) {
        setTimeout(() => { this.loadDocument(); }, 1000);
        return;
      }

      if (contentType === 'application/pdf') {
        this.embeddedContentUrl = this.originalContentUrl;
        return;
      }

      try {
        this.embeddedContentUrl = await getDownloadUrlByPath(`${documentPath}_webViewable.pdf`);
      } catch (e) {
        setTimeout(() => { this.loadDocument(); }, 1000);
      }
    },
  },

  async created() {
    this.loadDocument();
  },

  watch: {
    showAllPages() {
      this.page = this.showAllPages ? null : 1;
    },
  },

};
</script>
<style scoped>
  .pdfCard {
    display: flex;
    flex-direction: column;
  }

  .pdfContainer {
    display: flex;
    padding: 12px;
    margin-top: 8px;
  }

  .pdfContainer.loaded {
    background-color: #ccc;
  }

  .pdfViewer {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 0px;
  }

  .pdfViewer.loaded {
    height: 100vh;
  }

  ::v-deep .vue-pdf-embed > div {
    background-color: #ccc;
    margin-bottom: 1px;
  }

  .documentInfo {
    padding-bottom: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
</style>
